import { Box, Container, Grid, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { API } from '../../../global/config/api';
import { USER_DATA } from '../../../global/config/constants';
import { getFilters } from '../../../global/helpers/filters';
import { useServerCall } from '../../../global/helpers/hooks';
import { getByKey } from '../../../global/storage/StorageEx';
import { BaseHeader, XsButton, XsHeader, XsTableView } from '../../../global/ui';
import { IRecordsOverviewRow } from '../../../swagger/gd/record';
import { ListRequest, ListResponse } from '../../../swagger/shared/list';
import { IUserSession } from '../../../swagger/shared/user';
import { TColumnExt } from '../../../global/types/types';
import RequestsStore from './RequestsStore';
import ConsentSentDialog from './ConsentSentDialog';
import RequestTypeDialog from '../requestForm/RequestTypeDialog';
import RequestTypeDialogStore from '../requestForm/RequestTypeDialogStore';

const formatDateTime = (dateStr?: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Načíta company_id z uloženého user objektu v localStorage */
const getCompanyId = (): string | undefined => {
  const user = getByKey(USER_DATA) as IUserSession | undefined;
  return user?.company_data?.company_id ?? undefined;
};

/** Zostaví ListRequest telo pre /gd/record/overview */
const buildOverviewRequest = (): ListRequest => {
  const companyId = getCompanyId();
  const filterExprs = companyId ? [`company_id=${companyId}`] : [];
  return {
    ...getFilters(filterExprs)
  };
};

function Requests() {
  const { t } = useTranslation();
  const history = useHistory();

  const requestsCall = useServerCall<ListRequest, ListResponse<IRecordsOverviewRow>>({
    key: 'requests_overview',
    mode: 'auto',
    request: {
      method: 'POST',
      url: API.requestsOverview(),
      data: buildOverviewRequest()
    },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (response) => {
      RequestsStore.setData(response.data?.rows ?? []);
    }
  });

  const refetchList = () => {
    requestsCall.refetch();
  };

  const columns: TColumnExt<IRecordsOverviewRow>[] = [
    { Header: 'requests.col_sequence', accessor: 'request_sequence', width: 50 },
    {
      Header: 'requests.col_sent_date_time',
      accessor: 'sent_date_time',
      width: 160,
      Cell: ({ value }: any) => <span>{formatDateTime(value)}</span>
    },
    { Header: 'requests.col_request_number', accessor: 'request_number', width: 160 },
    { Header: 'requests.col_status_name_ext', accessor: 'status_name_ext', width: 160 },
    {
      Header: 'requests.col_start',
      accessor: 'start',
      width: 160,
      Cell: ({ value }: any) => <span>{formatDateTime(value)}</span>
    },
    {
      // Akcie — len ikona "odoslať" (paper-plane), verný port requests.js:47-66
      Header: 'requests.col_actions',
      accessor: 'request_id',
      width: 80,
      align: 'center',
      Cell: ({ row }: any) => {
        const r: IRecordsOverviewRow = row.original;
        if (r.status_code === 'inp') {
          return (
            <i
              className='fas fa-paper-plane fa-lg pointer'
              title={t('requests.action_send_title')}
              onClick={(e) => {
                e.stopPropagation();
                RequestsStore.openConsent(r.request_id);
              }}
            />
          );
        }
        return null;
      }
    }
  ];

  const tableData = {
    rows: RequestsStore.data,
    totalRows: RequestsStore.data.length
  };

  const user = getByKey(USER_DATA) as IUserSession | undefined;
  const companyIdentifier: string = user?.company_data?.identifier ?? '';

  return (
    <>
      <BaseHeader />
      <XsHeader />
      <Container maxWidth='lg'>
        <Grid container justifyContent='space-between' alignItems='center'>
          <Grid item>
            <Box pt={2} pb={2}>
              <Typography variant='h4'>
                {t('requests.title')}
                {companyIdentifier ? ` (IČO: ${companyIdentifier})` : ''}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <XsButton
              fullWidth={false}
              label='requests.new_request_button'
              variant='contained'
              icon={<i className='fas fa-plus' />}
              onClick={() => RequestTypeDialogStore.open()}
            />
          </Grid>
        </Grid>
        <Box pt={2}>
          <Grid container>
            <Grid item xs={12}>
              <Box pt={1}>
                <XsTableView<IRecordsOverviewRow>
                  name='requests_table'
                  control='client'
                  columns={columns}
                  data={tableData}
                  pagination
                  initState={{ pageSize: 20 }}
                  loading={requestsCall.isPending}
                  onRowClick={(row) => history.push(`/request?id=${row.request_id}`)}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
      <ConsentSentDialog onSent={refetchList} />
      <RequestTypeDialog />
    </>
  );
}

export default observer(Requests);
